import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { channelsApi } from "../api/channels";

const normalize = (value) => String(value || "").trim().toLowerCase();

const containsTerm = (row, term) => {
  const q = normalize(term);
  if (!q) return true;
  const hay = [
    row?.name,
    row?.mobile,
    row?.address,
    row?.city,
    row?.channelId,
    row?.businessName,
  ]
    .map((v) => normalize(v))
    .filter(Boolean)
    .join(" | ");
  return hay.includes(q);
};

export default function useChannelPartnerAutoSuggest({
  limit = 25,
  type = "Dealer",
} = {}) {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef(null);
  const allRef = useRef(null);
  const requestSeqRef = useRef(0);

  const fetchAll = useCallback(async () => {
    if (Array.isArray(allRef.current)) return allRef.current;
    const response = await channelsApi.getAll({
      type,
      status: "Active",
      limit: 10000,
      skip: 0,
    });
    const data = response?.data || response?.channels || [];
    allRef.current = Array.isArray(data) ? data : [];
    return allRef.current;
  }, [type]);

  useEffect(() => {
    fetchAll().catch(() => {});
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [fetchAll]);

  const search = useCallback(
    (term) => {
      const q = String(term || "").trim();
      if (timerRef.current) clearTimeout(timerRef.current);

      if (!q || q.length < 3) {
        setPartners([]);
        setLoading(false);
        return;
      }

      timerRef.current = setTimeout(async () => {
        const requestId = ++requestSeqRef.current;
        setLoading(true);
        try {
          const response = await channelsApi.search(q, type);
          let rows = response?.data || [];
          if (!Array.isArray(rows) || rows.length === 0) {
            const all = await fetchAll();
            rows = (all || []).filter((item) => containsTerm(item, q));
          }
          if (requestId !== requestSeqRef.current) return;
          setPartners(rows);
        } catch {
          if (requestId !== requestSeqRef.current) return;
          setPartners([]);
        } finally {
          if (requestId !== requestSeqRef.current) return;
          setLoading(false);
        }
      }, 240);
    },
    [fetchAll, type],
  );

  const options = useMemo(
    () =>
      (partners || []).map((p) => ({
        value: p?.name || "",
        label: [
          p?.name || "Unknown",
          p?.channelId,
          p?.mobile,
          p?.type,
        ]
          .filter(Boolean)
          .join(" · "),
        partner: p,
      })),
    [partners],
  );

  const getByName = useCallback(
    (name) => {
      const n = normalize(name);
      const pool = [
        ...(Array.isArray(partners) ? partners : []),
        ...(Array.isArray(allRef.current) ? allRef.current : []),
      ];
      return pool.find((p) => normalize(p?.name) === n) || null;
    },
    [partners],
  );

  return {
    loading,
    partners,
    options,
    search,
    getByName,
  };
}

